/* eslint-disable complexity */
import React, { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import Dialog from 'carbon-react/lib/components/dialog';
import Form from 'carbon-react/lib/components/form';
import Button from 'carbon-react/lib/components/button';

import { UILoader } from '../loader';

import styles from './ui-confirm-dialog.scss';

export enum DIALOG_ACTIONS {
  DELETE = 'delete',
  SUBMIT = 'submit'
}

interface UIConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  title?: ReactNode;
  description?: ReactNode;
  cancelButtonText?: ReactNode;
  submitButtonText?: ReactNode;
  submitButtonId?: string;
  isLoading?: boolean;
  modalSize?:
    | 'auto'
    | 'medium'
    | 'large'
    | 'small'
    | 'extra-small'
    | 'medium-small'
    | 'medium-large'
    | 'extra-large'
    | 'maximise';
  action?: DIALOG_ACTIONS;
  disabled?: boolean;
}

export const UIConfirmDialog = ({
  action = DIALOG_ACTIONS.DELETE,
  modalSize = 'medium',
  disabled = false,
  isLoading,
  isOpen,
  onClose,
  onSubmit,
  title,
  description,
  cancelButtonText,
  submitButtonText,
  submitButtonId
}: UIConfirmDialogProps) => {
  const intl = useIntl();

  const defaultTitle = intl.formatMessage({
    id: 'confirm_dialog.default_title',
    defaultMessage: 'Are you sure you want to delete this?'
  });

  const defaultDescription = intl.formatMessage({
    id: 'confirm_dialog.default_description',
    defaultMessage: 'Warning: this action is irreversible and you will lose all of the data associated.'
  });
  const defaultCancelButtonText = intl.formatMessage({
    id: 'common.cancel',
    defaultMessage: 'Cancel'
  });
  const defaultSubmitButtonText = intl.formatMessage({
    id: 'common.confirm',
    defaultMessage: 'Confirm'
  });

  if (isLoading) {
    return (
      <Dialog
        open={isOpen}
        onCancel={onClose}
        size={modalSize}
        aria-label={intl.formatMessage({
          id: 'common.confirm_dialog.aria_label',
          defaultMessage: 'Confirmation dialog'
        })}
      >
        <UILoader />
      </Dialog>
    );
  }

  return (
    <Dialog
      open={isOpen}
      onCancel={onClose}
      size={modalSize}
      aria-label={intl.formatMessage({
        id: 'common.confirm_dialog.aria_label',
        defaultMessage: 'Confirmation dialog'
      })}
      title={<h2 className={styles.title}>{title || defaultTitle}</h2>}
    >
      <Form
        onSubmit={() => null}
        leftSideButtons={
          <Button onClick={onClose} buttonType="secondary">
            {cancelButtonText || defaultCancelButtonText}
          </Button>
        }
        saveButton={
          <Button
            disabled={disabled}
            aria-label={intl.formatMessage({ id: 'common.confirm', defaultMessage: 'Confirm' })}
            onClick={onSubmit}
            buttonType="primary"
            destructive={action === 'delete'}
            id={submitButtonId || 'confirm-dialog-submit'}
          >
            {submitButtonText || defaultSubmitButtonText}
          </Button>
        }
      >
        <div className={styles.descriptionContainer}>{description || defaultDescription}</div>
      </Form>
    </Dialog>
  );
};
